/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import { v4 as uuidv4 } from 'uuid'

import ModelLoader from './ModelLoader'

declare let THREE: any

//
// Represents an Object in 3D

export default class SimpleObject {
  id: string
  modelUrl: string
  scene: any
  renderer: any
  audioListener: any
  onClick: any
  tokenActionPerformedListener: any
  container: any
  loadingIcon: any
  loaderStep: number

  constructor(modelUrl: string, scene: any, renderer: any, audioListener: any) {
    this.id = uuidv4()
    // Store token object
    this.modelUrl = modelUrl

    // Store scene root
    this.scene = scene

    // Store renderer
    this.renderer = renderer

    // Store audio listener
    this.audioListener = audioListener

    // Set by the parent to receive on click events
    this.onClick = null

    // Set by the parent to receive token action events
    this.tokenActionPerformedListener = null

    // Should we resize all objects to fit into a 1x1x1 meter box?
    // this.ResizeObjects = config.resizeObjects !== undefined ? config.resizeObjects : true;

    // Create 3D object
    this.container = new THREE.Object3D()
    this.container.onClick = this.handleClick.bind(this)

    // Create loading animation
    this.loadingIcon = this.createLoader()
    this.container.add(this.loadingIcon)

    this.loaderStep = 0
  }

  /** @returns Promise */
  async load() {
    // Catch errors
    try {
      console.info('Rendering 3D object: ', this.modelUrl)
      // Load model
      const obj = await ModelLoader.load(this.modelUrl)

      obj.scale.set(10, 10, 10)
      // Attach scene to view
      this.container.add(obj)

      // Get bounding box of entire scene
      const box = new THREE.Box3()
      const vec = new THREE.Vector3()
      box.setFromObject(this.container)
      box.getSize(vec)
      const sceneRadius = vec.length()
      // const sceneRadius = box.getSize().length();
      console.info('Scene Radius: ', sceneRadius)

      // Scale the scene down
      const desiredRadius = 1
      const scale = desiredRadius / sceneRadius
      this.container.scale.setScalar(scale)
    } finally {
      // Remove loader
      this.container.remove(this.loadingIcon)
    }
  }

  /** Called every frame */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onRender3D(delta: any, scene: any, camera: any) {}

  /** Create 3D loading animation */
  createLoader() {
    // Create container
    const holder = new THREE.Object3D()

    // Create loading ball 1
    let geometry = new THREE.SphereGeometry(1, 32, 32)
    let material = new THREE.MeshBasicMaterial({ color: 0x4286f4 })
    const ball1 = new THREE.Mesh(geometry, material)
    holder.add(ball1)

    // Create loading ball 2
    geometry = new THREE.SphereGeometry(1, 32, 32)
    material = new THREE.MeshBasicMaterial({ color: 0x247a26 })
    const ball2 = new THREE.Mesh(geometry, material)
    holder.add(ball2)

    // Animate loading icon
    this.loaderStep = 0
    ball1.onBeforeRender = (e: any) => {
      // Increase loader step
      this.loaderStep += 0.1

      // Resize the balls
      ball1.scale.setScalar(Math.sin(this.loaderStep) * 0.5 + 0.5)
      ball2.scale.setScalar(Math.sin(this.loaderStep + Math.PI) * 0.5 + 0.5)
    }

    // Done
    return holder
  }

  /** Called when the user clicks on this token */
  handleClick() {
    // No animation rule for "click", pass on the event
    this.onClick()
  }
}
